/**
 * Which tabs a partner sees.
 *
 * EXTRACTED SO IT CAN BE TESTED. This lived inline in AppShell, where a mistake
 * is invisible until someone loads the page and quietly does not find a feature
 * — which is exactly how the Teams tab shipped built, deployed and unreachable.
 *
 * `settings.modules` NO LONGER HIDES ANYTHING. It used to be read as an
 * allowlist over every tab, so an org whose list was written before a feature
 * existed never got that tab: silently, once per feature, with the symptom
 * appearing as "the thing you just built isn't there". Every tab is part of the
 * product; nothing here is per-partner.
 *
 * The two org rules are about STATE, not entitlement. The one viewer rule is
 * about ROLE: a tab built on seat rows is an admin's (canOpenModule).
 */

import type { OrgRole } from "@/types/api";

export interface NavItem {
	module: string;
}

/** Who is looking: their role in the org, undefined while it is still loading. */
export interface NavViewer {
	role: OrgRole | undefined;
}

/** Where a tab the viewer may not open sends them, and where "/" lands. */
export const DEFAULT_PATH = "/seats";

/**
 * Tabs only an admin or owner may open. Teams shows who is on each team, and
 * the server gives a member no seat rows (GET /members is admin-only), so a
 * member's Teams screen would only say that every team is empty.
 */
const ADMIN_ONLY_MODULES: ReadonlySet<string> = new Set(["teams"]);

function isAdminRole(role: OrgRole | undefined): boolean {
	return role === "admin" || role === "owner";
}

/**
 * Whether this viewer may open the tab, in the nav and at its route. Fails
 * closed: an admin-only tab stays shut until the role is known.
 */
export function canOpenModule(module: string, viewer: NavViewer): boolean {
	return !ADMIN_ONLY_MODULES.has(module) || isAdminRole(viewer.role);
}

export function visibleNav<T extends NavItem>(
	items: readonly T[],
	org: {
		suspended: boolean;
		/** No contract yet: every tab would be an empty screen. */
		unstarted: boolean;
	},
	viewer: NavViewer,
): T[] {
	// Before setup there is no plan, no seats and no invoice. Showing none is
	// honest; showing six blanks is not. Checked FIRST: an org that never
	// started cannot be suspended over an invoice that was never issued.
	if (org.unstarted) return [];

	// A suspended org keeps exactly one screen. Offering the others hands
	// someone a menu of 403s at the moment they are least inclined to forgive
	// it — and Billing is the one thing that ends the suspension.
	if (org.suspended) return items.filter((i) => i.module === "billing");

	return items.filter((i) => canOpenModule(i.module, viewer));
}
